// =============================================================================
//  attribution-service.ts — fakturomania-landing analytics tracker
// =============================================================================
//  Co to robi:
//   - Wysyła "touch" (tj. zdarzenie page_view) do n8n endpointu po każdej zmianie strony
//   - Zachowuje stabilne session_id w cookie (180 dni, cross-subdomain)
//   - Łapie UTM-y, gclid/fbclid, referrer — żebyśmy wiedzieli skąd przyszedł użytkownik
//   - Fail-silent — w razie błędu NIGDY nie psuje strony (try/catch wszędzie)
//
//  Endpoint przyjmuje JSON i odpowiada zawsze 204 (No Content) — fail silent na poziomie HTTP.
//
//  Zgoda (FAK-28914, T3):
//   - fm_session to cookie ANALITYCZNE — zapisujemy je i wysyłamy touch WYŁĄCZNIE
//     po zgodzie na 'analytics' w banerze. Bez zgody touch() nic nie robi.
//
//  Integracja:
//   - gatsby-browser.js → onRouteUpdate woła touch()
//   - cookie-consent-service.ts → po każdej decyzji woła handleUpdateAttributionConsent(),
//     a gdy zgodę wyrażono w trakcie wizyty, woła touch() dla bieżącej strony
// =============================================================================


import { isLocalhost } from './environment-service'

// =============================================================================
//  Stałe konfiguracyjne — gdyby coś się zmieniło, edytuj tutaj
// =============================================================================

// Adres webhook'a w n8n — to tutaj wysyłamy każdy "touch". Zaczyna się od
// "fakturomania-" zgodnie z konwencją wszystkich naszych webhooków.
const N8N_ENDPOINT = 'https://n8n.fakturomania.pl/webhook/fakturomania-attribution-touch'

// Nazwa cookie w której trzymamy nasz session_id (UUID v4). Krótka, "fm_" prefix
// wskazuje że to nasze cookie (a nie GA, Freshdesk itp).
const SESSION_COOKIE = 'fm_session'

// Ile dni żyje cookie session_id. 180 dni = pół roku. Tak długi okres,
// bo user może wrócić do nas po miesiącach od pierwszej wizyty (consideration cycle B2B).
const SESSION_COOKIE_DAYS = 180

// Domain dla cookie z kropką na początku (".fakturomania.pl") — pozwala cookie
// dzielić się między fakturomania.pl (landing) a app.fakturomania.pl (apka).
// Dzięki temu user idzie z landingu do apki, a session_id się zachowuje.
//
// UWAGA: Na localhost / 127.0.0.1 (dev) przeglądarka ODRZUCA cookie z tym domain
// (security policy: domain musi pasować do host). Funkcja getCookieDomain() niżej
// auto-wykrywa dev environment i zwraca pusty string (= current host).
const COOKIE_DOMAIN_PROD = '.fakturomania.pl'

// Klucz w localStorage gdzie trzymamy aktualne zgody (analytics/marketing).
// Używamy localStorage bo jest większy i prostszy niż cookie do trzymania JSON-a.
const CONSENT_LS_KEY = 'fm_attribution_consent'


// =============================================================================
//  TypeScript types — opisują kształt obiektów (autocomplete + walidacja przy build)
// =============================================================================

// Stan zgód użytkownika. Necessary zawsze true (ten typ cookies jest wymagany prawnie).
interface AttributionConsent {
    necessary: boolean
    analytics: boolean
    marketing: boolean
}

// Parametry UTM z URL. Wszystkie opcjonalne — nie zawsze są w URL.
// Optional types ("?") oznaczają że pole może być undefined.
interface UTMParams {
    source?: string     // utm_source, np. "google", "facebook"
    medium?: string     // utm_medium, np. "cpc", "email", "organic"
    campaign?: string   // utm_campaign, np. "spring-sale-2026"
    term?: string       // utm_term, np. keyword z Google Ads
    content?: string    // utm_content, np. wariant kreatywki (A/B test)
}

// Click ID-ki od platform reklamowych. Pozwalają potem dopasować
// nasze konwersje do wyświetleń konkretnej reklamy.
interface ClickIds {
    gclid?: string      // Google Ads click ID
    fbclid?: string     // Facebook/Instagram click ID
    msclkid?: string    // Microsoft (Bing) Ads click ID
}

// Informacje o urządzeniu użytkownika.
interface DeviceInfo {
    type: string             // "desktop" | "mobile" | "tablet" | "unknown"
    browser_family: string   // "Chrome" | "Firefox" | "Safari" | "Edge" | "other"
    screen_resolution: string // np. "1920x1080"
}


// =============================================================================
//  Cookie helpers — pomocnicze funkcje do czytania/zapisywania cookies
// =============================================================================

/**
 * Czyta wartość cookie po nazwie. Zwraca null jeśli nie istnieje.
 * Cookies w document.cookie są jednym stringiem typu: "name1=val1; name2=val2; name3=val3"
 */
function getCookie(name: string): string | null {
    // Sprawdź czy uruchomiamy się w przeglądarce. W Gatsby build (SSR) document nie istnieje.
    if (typeof document === 'undefined') return null

    // Regex łapie "name=value" — ^| oznacza "początek stringa lub spacja przed".
    // Druga grupa łapiąca ([^;]+) bierze wszystko aż do średnika (czyli value cookie).
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))

    // Jeśli match istnieje — zwróć value (drugą grupę) zdekodowane z URL encoding.
    // Inaczej null (cookie nie znalezione).
    return match ? decodeURIComponent(match[2]) : null
}

/**
 * Auto-detect cookie domain — produkcja vs dev.
 * Na localhost/127.0.0.1 NIE możemy ustawić domain=.fakturomania.pl (browser security),
 * więc zwracamy pusty string (= cookie dla obecnego hosta tylko, np. localhost).
 * Na produkcji zwracamy .fakturomania.pl żeby cookie było cross-subdomain.
 */
function getCookieDomain(): string {
    // SSR fallback — w Gatsby build nie ma window
    if (typeof window === 'undefined') return COOKIE_DOMAIN_PROD

    // Wyciągnij hostname (np. "localhost", "fakturomania.pl", "app.fakturomania.pl")
    const host = window.location.hostname

    // Dev environments — localhost, 127.0.0.1, *.local (Linux/Mac mDNS)
    // Brak domain w cookie = "tylko ten host" — przeglądarka akceptuje
    if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
        return ''
    }

    // Produkcja — pełna domena z kropką (cross-subdomain z app.fakturomania.pl)
    return COOKIE_DOMAIN_PROD
}

/**
 * Zapisuje cookie z podanym name, value, ile dni i na jakiej domenie.
 * Cookie jest zwykłym stringiem przypisanym do document.cookie — przeglądarka rozumie format.
 */
function setCookie(name: string, value: string, days: number, domain: string): void {
    // SSR safety — w Gatsby build nie ma document
    if (typeof document === 'undefined') return

    // Oblicz datę wygaśnięcia: teraz + N dni × 86400000ms (sekund w dniu × 1000).
    // toUTCString daje format który przeglądarka rozumie ("Wed, 21 Oct 2026 07:28:00 GMT")
    const expires = new Date(Date.now() + days * 86400000).toUTCString()

    // Część bazowa cookie — name=value + expires + path + SameSite (zawsze obecne)
    // - encodeURIComponent: zabezpiecza wartość przed znakami specjalnymi (=, ;, spacje)
    // - path=/: cookie dostępne na każdej ścieżce strony
    // - SameSite=Lax: cookie wysyła się przy normalnej nawigacji (klik w link), ale nie w cross-site POST
    const base = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`

    // Dopisz domain TYLKO jeśli niepusty (na localhost domain musi być pusty,
    // inaczej przeglądarka odrzuca cookie z błędem "rejected for invalid domain").
    const domainPart = domain ? `; domain=${domain}` : ''

    // Złożony cookie string przypisany do document.cookie — przeglądarka go zapisuje
    document.cookie = base + domainPart
}


// =============================================================================
//  Session ID — UUID v4 w cookie
// =============================================================================

/**
 * Generuje losowy UUID v4 (np. "550e8400-e29b-41d4-a716-446655440000").
 * Format: 8-4-4-4-12 hex characters. v4 oznacza że jest losowe (nie z czasu/MAC).
 */
function generateUUIDv4(): string {
    // Nowoczesna metoda: crypto.randomUUID() (Chrome 92+, Safari 15.4+, Firefox 95+).
    // To jest cryptographically secure — niemożliwe do przewidzenia.
    if (typeof crypto !== 'undefined' && (crypto as any).randomUUID) {
        return (crypto as any).randomUUID()
    }

    // Fallback dla starszych przeglądarek — Math.random nie jest crypto-secure,
    // ale dla session_id (nie zabezpieczenie kryptograficzne) to OK.
    // Replace zamienia każde "x" i "y" na losowy hex char zgodny z RFC4122 v4.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        // Losowa liczba 0-15
        const r = Math.random() * 16 | 0
        // Dla "x" — dowolna; dla "y" — musi być 8/9/a/b (wymagania RFC4122 v4)
        const v = c === 'x' ? r : (r & 0x3 | 0x8)
        // Zamień na hex (16 → "f", 10 → "a")
        return v.toString(16)
    })
}

/**
 * Pobiera istniejący session_id z cookie LUB tworzy nowy i zapisuje.
 * Dzięki temu user ma stabilne ID przez 180 dni (lub do wyczyszczenia cookies).
 */
function getOrCreateSessionId(): string {
    // Spróbuj wyczytać istniejące cookie
    let sid = getCookie(SESSION_COOKIE)

    // Jeśli nie ma — wygeneruj nowy i zapisz
    if (!sid) {
        sid = generateUUIDv4()
        // getCookieDomain() zwraca '.fakturomania.pl' na produkcji, '' na localhost
        setCookie(SESSION_COOKIE, sid, SESSION_COOKIE_DAYS, getCookieDomain())
    }

    // Zwróć (istniejący lub świeżo utworzony)
    return sid
}


// =============================================================================
//  URL parsing — wyciąganie UTM/click_ids z aktualnego adresu
// =============================================================================

/**
 * Wyciąga UTM parameters z URL. Obiekt URL ma .searchParams które jest
 * jak Map — ma .get(nazwa) zwracające string lub null.
 * Konwertujemy null → undefined żeby JSON.stringify pominął te pola
 * (zamiast wysyłać "utm_source": null).
 */
function parseUTM(url: URL): UTMParams {
    const params = url.searchParams
    return {
        source:   params.get('utm_source')   || undefined,
        medium:   params.get('utm_medium')   || undefined,
        campaign: params.get('utm_campaign') || undefined,
        term:     params.get('utm_term')     || undefined,
        content:  params.get('utm_content')  || undefined,
    }
}

/**
 * Wyciąga click_ids z URL — analogicznie do UTM, ale z innych pól.
 * Te ID-ki dodają reklamy automatycznie do URL po kliknięciu.
 */
function parseClickIds(url: URL): ClickIds {
    const params = url.searchParams
    return {
        gclid:   params.get('gclid')   || undefined,
        fbclid:  params.get('fbclid')  || undefined,
        msclkid: params.get('msclkid') || undefined,
    }
}


// =============================================================================
//  Device detection — proste UA parsing bez zewnętrznych bibliotek
// =============================================================================

/**
 * Wykrywa typ urządzenia, przeglądarkę i rozdzielczość ekranu.
 * Używamy navigator.userAgent — string opisujący przeglądarkę
 * (np. "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 ... Chrome/120 ...").
 */
function getDeviceInfo(): DeviceInfo {
    // SSR safety — w Gatsby build navigator nie istnieje
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return { type: 'unknown', browser_family: 'unknown', screen_resolution: 'unknown' }
    }

    // Pełny user agent string — różne przeglądarki różnie się przedstawiają
    const ua = navigator.userAgent

    // iPad i Android tablet (z "Android" ale bez "Mobile" w UA — Android phones mają "Mobile")
    const isTablet = /iPad|Android(?!.*Mobile)/i.test(ua)

    // Mobile = iPhone, iPod, Android phone (NOT już wykryte jako tablet)
    const isMobile = !isTablet && /Mobi|Android|iPhone|iPod/i.test(ua)

    // Określ typ na podstawie powyższych flag (kolejność ważna: tablet > mobile > desktop)
    const type = isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop'

    // Wykryj przeglądarkę. Edge musi być przed Chrome bo Edge UA zawiera też "Chrome"
    // (Edge jest na silniku Chromium). Safari musi być po Chrome bo Chrome UA też ma "Safari".
    let browser_family = 'other'
    if (/Edg\//.test(ua)) browser_family = 'Edge'
    else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser_family = 'Chrome'
    else if (/Firefox\//.test(ua)) browser_family = 'Firefox'
    else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser_family = 'Safari'

    // Rozdzielczość ekranu (fizyczna, nie viewport) — np. "1920x1080".
    // Jest typeof screen check bo czasem (rzadko, np. headless browser) screen może nie istnieć.
    const screen_resolution = typeof screen !== 'undefined'
        ? `${screen.width}x${screen.height}`
        : 'unknown'

    return { type, browser_family, screen_resolution }
}


// =============================================================================
//  Consent state — odczyt/zapis zgód użytkownika do localStorage
// =============================================================================

/**
 * Czyta aktualny stan zgód z localStorage.
 * Domyślnie (przed pierwszym accept) zwracamy "ostrożny" tryb: tylko necessary.
 * NIE zmieniaj na true — bez zgody touch() nie może nic wysłać (Polityka cookies, § 3 ust. 2).
 */
function getConsent(): AttributionConsent {
    // SSR safety — w Gatsby build localStorage nie istnieje
    if (typeof localStorage === 'undefined') {
        return { necessary: true, analytics: false, marketing: false }
    }

    // try/catch bo localStorage może rzucić wyjątkiem (private browsing, quota)
    try {
        // Odczytaj string z localStorage
        const raw = localStorage.getItem(CONSENT_LS_KEY)

        // Brak zapisanych zgód — domyślnie tylko necessary.
        // ⚠️ Bez zapisanej zgody analytics MUSI być false (Polityka cookies, § 3 ust. 2)
        if (!raw) {
            return { necessary: true, analytics: false, marketing: false }
        }

        // Parsuj JSON i zwróć z type assertion (TypeScript nie weryfikuje runtime)
        return JSON.parse(raw) as AttributionConsent
    } catch (_e) {
        // Cokolwiek pójdzie nie tak — fail silent, zwróć defaults
        return { necessary: true, analytics: false, marketing: false }
    }
}

/**
 * Zapisuje nowy stan zgód do localStorage. Wywoływane po consent accept/change.
 */
function saveConsent(analytics: boolean, marketing: boolean): void {
    // SSR safety
    if (typeof localStorage === 'undefined') return

    // Zbuduj obiekt — necessary zawsze true (RODO wymaga że wymagane cookies muszą być)
    const consent: AttributionConsent = { necessary: true, analytics, marketing }

    // try/catch bo localStorage.setItem może rzucić (quota exceeded, disabled w incognito)
    try {
        localStorage.setItem(CONSENT_LS_KEY, JSON.stringify(consent))
    } catch (_e) {
        // Fail silent — nie blokujemy strony jeśli localStorage padnie
    }
}


// =============================================================================
//  Public API — funkcje które exportujemy do użycia w innych plikach
// =============================================================================

/**
 * Wysyła page_view "touch" do n8n. NIE blokuje strony — sendBeacon jest fire-and-forget.
 * Wywoływane z gatsby-browser.js (onRouteUpdate) i z cookie-consent-service.ts (zgoda w trakcie wizyty).
 * Bez zgody na analitykę nic nie robi.
 *
 * @param landingPath — opcjonalna ścieżka strony (jeśli nie podana, wezmie z window.location.pathname)
 */
export function touch(landingPath?: string): void {
    // SSR safety — w Gatsby build window nie istnieje, omijamy
    if (typeof window === 'undefined') return

    // Na localhost nie wysyłamy touchy do produkcyjnego n8n
    if (isLocalhost()) return

    // try/catch wokół całości — KRYTYCZNE: attribution NIGDY nie może crashnąć strony
    try {
        // Parsuj aktualny URL — daje wygodny dostęp do pathname, searchParams, hash, etc.
        const url = new URL(window.location.href)

        // Pobierz aktualny stan zgód. Bez zgody na analitykę nie zapisujemy fm_session
        // i nic nie wysyłamy do n8n (FAK-28914, T3).
        const consent = getConsent()
        if (!consent.analytics) return

        const sid = getOrCreateSessionId()

        // Zbuduj payload zgodny z kontraktem n8n /webhook/fakturomania-attribution-touch.
        // Pola dokładnie takie jak walidacja w n8n flow oczekuje.
        const payload = {
            // Unikalny ID tego zdarzenia (idempotency w n8n — duplicate retry skipnie)
            event_id: generateUUIDv4(),

            // Stable ID sesji (cookie)
            session_id: sid,

            // Kiedy wysyłamy (ISO format z Z na końcu = UTC)
            emitted_at: new Date().toISOString(),

            // Landing nie zna user_id — to backend wpisze później po rejestracji.
            // "as string | null" — type assertion: mówimy TypeScriptowi że to pole może być stringiem
            // w przyszłości (gdy ktoś rozszerzy kod), inaczej TS zacieśniłby typ do literal "null".
            user_id: null as string | null,

            // UTM-y z URL (lub puste jeśli nie ma w URL)
            utm: parseUTM(url),

            // Click ID-ki od platform reklamowych
            click_ids: parseClickIds(url),

            // Skąd przyszedł user (np. "https://www.google.com/" jeśli kliknął w organic search).
            // null jeśli direct visit (wpisał URL ręcznie lub bookmark).
            referrer: document.referrer || null,

            // Ścieżka strony bez query/hash (np. "/cennik")
            landing_path: landingPath || url.pathname,

            // Pełny URL z query, hash, etc.
            full_url: url.href,

            // Info o urządzeniu
            device: getDeviceInfo(),

            // Aktualne zgody — backend wie co może z tymi danymi zrobić
            consent: consent
        }

        // Preferowany sposób: navigator.sendBeacon — wysyła w tle, nie blokuje nawigacji.
        // Idealne dla page_view bo user może już klikać dalej, a my chcemy mieć dane.
        //
        // ⚠️ CORS TRICK: Używamy type "text/plain" zamiast "application/json".
        // Powód: "application/json" to "non-simple request" → przeglądarka robi PREFLIGHT
        // (osobny OPTIONS request), co wymaga że n8n umie obsłużyć OPTIONS + zwrócić CORS.
        // "text/plain" jest CORS-safelisted → idzie BEZPOŚREDNIO bez preflight.
        // n8n czyta body niezależnie od Content-Type i parsuje JSON wewnątrz workflow.
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
            // sendBeacon przyjmuje Blob — pakujemy JSON-a jako tekst plain
            const blob = new Blob([JSON.stringify(payload)], { type: 'text/plain;charset=UTF-8' })
            navigator.sendBeacon(N8N_ENDPOINT, blob)
        } else {
            // Fallback dla starszych przeglądarek (iOS < 13 itp.)
            // keepalive: true — zachowuje request nawet gdy strona się zamknie
            // credentials: omit — nie wysyłaj cookies/auth z naszej domeny do n8n
            // text/plain content-type — analogicznie omija preflight
            fetch(N8N_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
                body: JSON.stringify(payload),
                keepalive: true,
                credentials: 'omit'
            }).catch(() => { /* fail silent — błędy sieci nie psują strony */ })
        }
    } catch (e) {
        // KRYTYCZNE: jeśli cokolwiek tu padnie — nie wybuchaj strony.
        // W trybie deweloperskim wypisz w konsoli, w produkcji cisza.
        if (process.env.NODE_ENV === 'development') {
            // tslint:disable-next-line:no-console
            console.warn('[attribution] touch failed (silenced):', e)
        }
    }
}

/**
 * Hook do cookie-consent-service.ts — wywoływany po każdej decyzji w banerze
 * (i przy wejściu, gdy zgoda jest już zapisana). Tylko zapisuje stan zgód —
 * touch dla bieżącej strony wysyła cookie-consent-service, gdy zgodę wyrażono w trakcie wizyty.
 */
export function handleUpdateAttributionConsent(
    analyticsGranted: boolean,
    marketingGranted: boolean
): void {
    // Zapisz nowy stan
    saveConsent(analyticsGranted, marketingGranted)
}

/**
 * Diagnostic helper — do troubleshootingu z konsoli przeglądarki.
 * Wywołaj: window.__fmAttributionDebug() w DevTools.
 * Pokaże: aktualny session_id, consent, parsed UTM, device info.
 * NIE używać w produkcji code path — tylko do debugowania.
 */
export function __fmAttributionDebug(): object {
    if (typeof window === 'undefined') return { error: 'SSR' }
    const url = new URL(window.location.href)
    return {
        session_id: getCookie(SESSION_COOKIE),
        consent: getConsent(),
        utm: parseUTM(url),
        click_ids: parseClickIds(url),
        device: getDeviceInfo(),
        endpoint: N8N_ENDPOINT
    }
}

// Wystawiamy debug helper na window żeby był dostępny z DevTools console.
// (window as any) — TypeScript nie zna typu __fmAttributionDebug na window, force cast.
if (typeof window !== 'undefined') {
    (window as any).__fmAttributionDebug = __fmAttributionDebug
}


// WEBPACK FOOTER //
// ./src/services/attribution-service.ts